import React, { useEffect, useMemo, useRef, useState } from 'react';

import moment from 'moment';
import Chart from 'react-apexcharts';
import { MdOutlineRefresh } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Container from '~/components/container';
import ContainerFlex from '~/components/containerFlex';
import Dialog from '~/components/dialog';
import LoadingComponents from '~/components/loadingInComponents';
import Select from '~/components/select';
import useApi from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';
import Constants from '~/utils/constants';
import { useUserRole } from '~/hooks/contexts/useUserRole';
import { abbreviateNumber, currencyMask } from '~/utils/masks';
import * as S from './styles';
import { runDownloadTask } from '~/utils/runDownloadTask';

interface MonthBilling {
  titulo: string;
  quantidadeTransacoes: number;
  valorUnitario: number;
  valorTotal: number;
  legenda: {
    descricao: string;
    titulo: string;
  };
}
interface Last6MonthBilling {
  periodo: string;
  valor: number;
}

const TooltipArrow = styled.div`
  position: absolute;
  top: -20px;
  width: 35px;
  height: 20px;
  box-shadow: -1px -1px 2px rgba(0, 0, 0, 0.05);

  clip-path: polygon(0% 100%, 50% 50%, 100% 100%);
  background-color: #1d3557;
`;
const monthsOptions = Array.from({ length: 6 }, (_, i) => {
  const date = moment().subtract(i, 'months');
  const id = date.format('YYYY-MM');
  const value =
    date
      .toDate()
      .toLocaleDateString('pt-BR', { month: 'long' })
      .replace(/^./, (str) => str.toUpperCase()) +
    '/' +
    date.format('YYYY');
  return { id, value };
});
const DialogDownloadReport = ({ open, onClose }) => {
  const options = [
    { id: 'BENEFICIOS', value: 'Benefícios' },
    { id: 'CARTEIRA_DIGITAL', value: 'Carteira Digital' },
    { id: 'SAQUES', value: 'Saques' },
    { id: 'TRANSACOES', value: 'Transações' },
    { id: 'CLIENTES_ATIVOS', value: 'Clientes Ativos' },
    { id: 'MOTOR_CREDITO', value: 'Motor Crédito' },
    { id: 'MOTOR_EMBOSSING', value: 'Motor Embossing' },
    { id: 'NOVOS_CLIENTES', value: 'Novos Clientes' },
    { id: 'TRANSACOES_SPC', value: 'Transações SPC' },
    { id: 'LIVENESS_FACEMATCH', value: 'Liveness com FaceMatch' },
  ];
  const [form, setForm] = useState({
    tipo: '',
    data: '',
  });
  const GetReport = useApi({
    url: Endpoints.sustenance.getDownloadReport,
    enabled: false,
  });

  const onReportDownload = async (params) =>
    runDownloadTask(async () => {
      const response = await GetReport.callApi({
        ...params,
      });
      if (response.data?.base64) {
        const link = document.createElement('a');
        link.href = 'data:text/csv;base64,' + response.data.base64;
        link.download = response.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
  return (
    <Dialog
      open={open}
      negativeLabel="Voltar"
      positiveAction={() => onReportDownload(form)}
      positiveLabel="Download"
      negativeAction={onClose}
      buttonDisabledPositive={!form.data || !form.tipo}
      Loading={GetReport.loading}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <span style={{ fontSize: '1.5rem', fontWeight: 600 }}>
          Escolha o período e o tipo de relatório
        </span>
        <Select
          value={form.data}
          label="Mês e ano"
          options={monthsOptions}
          onChange={(v) => setForm((prev) => ({ ...prev, data: v }))}
        />
        <Select
          value={form.tipo}
          label="Tipo"
          options={options}
          onChange={(v) => setForm((prev) => ({ ...prev, tipo: v }))}
        />
      </div>
    </Dialog>
  );
};
const DownloadIcon = () => (
  <svg
    width="35"
    height="35"
    viewBox="0 0 35 35"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.4999 23.3335L10.2083 16.0418L12.2499 13.9272L16.0416 17.7189V5.8335H18.9583V17.7189L22.7499 13.9272L24.7916 16.0418L17.4999 23.3335ZM8.74992 29.1668C7.94784 29.1668 7.2612 28.8812 6.69002 28.3101C6.11884 27.7389 5.83325 27.0522 5.83325 26.2502V21.8752H8.74992V26.2502H26.2499V21.8752H29.1666V26.2502C29.1666 27.0522 28.881 27.7389 28.3098 28.3101C27.7386 28.8812 27.052 29.1668 26.2499 29.1668H8.74992Z"
      fill="#E4003A"
    />
  </svg>
);
const Tooltip = ({ text, title }) => {
  const iconRef = useRef(null);
  const boxRef = useRef(null);
  const [boxStyle, setBoxStyle] = useState({});

  const updatePosition = () => {
    if (!boxRef.current || !iconRef.current) return;

    const boxRect = boxRef.current.getBoundingClientRect();

    const isOverflowingRight = boxRect.right > window.innerWidth;
    const isOverflowingLeft = boxRect.left < 0;

    if (isOverflowingRight) {
      setBoxStyle({ right: 0, left: 'auto', transform: 'none' });
    } else if (isOverflowingLeft) {
      setBoxStyle({ left: 0, right: 'auto', transform: 'none' });
    } else {
      setBoxStyle({
        left: '50%',
        right: 'auto',
        transform: 'translateX(-50%)',
      });
    }
  };

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, []);

  return (
    <S.TooltipContainer ref={iconRef}>
      <S.TooltipIcon>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V9H11V15ZM11 7H9V5H11V7Z"
            fill="#D4313E"
          />
        </svg>
      </S.TooltipIcon>
      <S.TooltipBox ref={boxRef} style={boxStyle}>
        <S.TooltipTitle>{title}</S.TooltipTitle>
        <S.TooltipText>{text}</S.TooltipText>
      </S.TooltipBox>
    </S.TooltipContainer>
  );
};

const CardInfo = ({ item }: { item: MonthBilling }) => {
  return (
    <ContainerFlex
      style={{
        minHeight: '100px',
        minWidth: '150px',
      }}
      center
      gap="4"
    >
      <h2>{item.quantidadeTransacoes}</h2>
      <span style={{ color: '#AEAEAE' }}>{item.titulo}</span>
      <span style={{ color: '#AEAEAE' }}>
        R$ {currencyMask(item.valorTotal)}
      </span>
      <Tooltip title={item.legenda.titulo} text={item.legenda.descricao} />
    </ContainerFlex>
  );
};
const StoreOpenningDashboard: React.FC = () => {
  const { hasAnyRole } = useUserRole();
  const history = useHistory();
  const validateRoles = () =>
    hasAnyRole([
      Constants.ROLE_ADMIN,
      Constants.ROLE_SUPERVISOR,
      Constants.ROLE_LIDER_VENDAS,
    ]);

  const { data, loading: isLoadingMonthly, refetch } = useApi<MonthBilling[]>({
    url: Endpoints.sustenance.getMonthlyBilling,
    body: {
      data: moment().format('YYYY-MM'),
    },
  });
  const {
    data: last6MonthsData,
    loading: isLoading6Months,
    refetch: refetch6,
  } = useApi<Last6MonthBilling[]>({
    url: Endpoints.sustenance.getLastSixMonthsBilling,
  });
  const periodInitialState = moment().format('YYYY-MM');

  const [period, setPeriod] = useState(periodInitialState);

  const refetchAll = () => {
    refetch();
    refetch6();
    setPeriod(periodInitialState);
  };
  const loading = isLoadingMonthly || isLoading6Months;

  const [chartData, setChartData] = useState({
    series: [],
    options: {},
  });
  const formatData = async () => {
    if (!last6MonthsData?.length) {
      return;
    }

    const obj = {
      series: [
        {
          name: 'Valor',
          data: last6MonthsData.map((item) => item.valor),
        },
      ],
      options: {
        colors: ['#0074BD'],
        plotOptions: {
          bar: {
            columnWidth: '90%',
            distributed: true,
            borderRadius: 4,
            dataLabels: {
              position: 'top',
            },
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: -25,
          style: {
            fontSize: '12px',
            colors: ['#304758'],
          },
          formatter(val) {
            return `R$ ${abbreviateNumber(val)}`;
          },
        },
        legend: {
          show: false,
        },
        xaxis: {
          categories: last6MonthsData.map((item) => item.periodo),
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          tooltip: {
            enabled: true,
          },
          labels: {
            show: true,
            formatter(val) {
              const [month, year] = val.split('/');
              const date = new Date(`${year}-${month}-01`);

              const monthName = date.toLocaleDateString('pt-BR', {
                month: 'long',
                timeZone: 'UTC',
              });

              return `${
                monthName.charAt(0).toUpperCase() + monthName.slice(1)
              }/${year}`;
            },
          },
        },
        yaxis: {
          axisBorder: {
            show: true,
          },
          axisTicks: {
            show: true,
          },
          labels: {
            show: true,
            formatter(val) {
              return `R$ ${abbreviateNumber(val)}`;
            },
          },
        },
      },
    };

    if (data?.length > 0) {
      setChartData(obj);
    }
  };
  useEffect(() => {
    formatData();
  }, [last6MonthsData]);

  const [reportDialog, setReportDialog] = useState(false);

  const handleDialogClose = () => {
    setReportDialog(false);
  };

  const totalValue = useMemo(() => {
    if (!data || data.length === 0) return '0,00';
    const total = data.reduce((acc, item) => acc + item.valorTotal, 0);
    return currencyMask(total);
  }, [data]);
  return (
    <Container title="Dashboard B.I.">
      <DialogDownloadReport open={reportDialog} onClose={handleDialogClose} />
      {validateRoles() && (
        <>
          <ContainerFlex>
            <S.EndCardsItemsNoHover>
              <LoadingComponents status={loading} />
              <header
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <div className="title">
                  <h1>
                    Faturamento sustentação{' '}
                    {moment(period).toDate().toLocaleDateString('pt-BR', {
                      month: 'long',
                      timeZone: 'UTC',
                    })}
                  </h1>

                  <span className="title__update">
                    {`Atualizado: hoje às ${moment().format('HH:mm')}`}
                  </span>
                </div>
                <div
                  className="title"
                  style={{
                    display: 'flex',
                    gap: '8px',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <ContainerFlex style={{ minWidth: '150px' }}>
                    <Select
                      value={period}
                      label=""
                      options={monthsOptions}
                      onChange={(v) => {
                        setPeriod(v);
                        refetch({ data: v });
                      }}
                    />
                  </ContainerFlex>
                  <ContainerFlex row gap="8" center>
                    <h1>R$ {totalValue}</h1>
                    <ContainerFlex
                      onClick={() => refetchAll()}
                      role="button"
                      style={{
                        cursor: 'pointer',
                        width: 'fit-content',
                        flex: 0,
                      }}
                      row
                      center
                      gap="4"
                    >
                      <MdOutlineRefresh fontSize="2rem" />
                    </ContainerFlex>
                  </ContainerFlex>
                </div>
              </header>

              <ContainerFlex
                row
                gap="12"
                center
                wrap
                style={{ justifyContent: 'stretch' }}
              >
                {data?.map((item) => {
                  return <CardInfo item={item} />;
                })}
              </ContainerFlex>
            </S.EndCardsItemsNoHover>
            <S.EndCardsItemsNoHover style={{ marginTop: '1rem' }}>
              <LoadingComponents status={loading} />
              <header
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <div className="title">
                  <h1>Histórico de faturamento</h1>
                  <span className="title__update">
                    {`Atualizado: hoje às ${moment().format('HH:mm')}`}
                  </span>
                </div>
                <div
                  className="title"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setReportDialog(true)}
                >
                  <DownloadIcon />
                </div>
              </header>

              <ContainerFlex gap="12">
                <Chart
                  height={250}
                  type="bar"
                  series={chartData.series}
                  options={chartData.options}
                />
              </ContainerFlex>
            </S.EndCardsItemsNoHover>
          </ContainerFlex>
        </>
      )}
    </Container>
  );
};

export default StoreOpenningDashboard;
